'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Chrome,
  Download,
  Lock,
  MonitorPlay,
  RefreshCw,
  Scissors,
  Sparkles,
  Star
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { Button } from '@workspace/ui/components/customButton';

const vmLogoSrc = '/assets/homepage/vmlogo-icon.png';

const ROTATE_INTERVAL = 10000;
const PAUSE_DURATION = 10000;

const cards = [
  {
    icon: Chrome,
    title: 'Install Extension',
    description: 'Add VideoMule to Chrome and start recording instantly.',
    image: '/assets/homepage/chrome-ext/1-install.png'
  },
  {
    icon: MonitorPlay,
    title: 'Record Your Screen',
    description:
      'Select your screen, hide controls, and capture your workflow in one click.',
    image: '/assets/homepage/chrome-ext/2-record.png'
  },
  {
    icon: Download,
    title: 'Preview & Download',
    description:
      'Watch your recording instantly. Download as MP4 or export JSON data.',
    image: '/assets/homepage/chrome-ext/3-preview.png'
  },
  {
    icon: Scissors,
    title: 'Edit Instantly',
    description:
      'Trim, zoom, change background, adjust layout, and export quickly.',
    image: '/assets/homepage/chrome-ext/4-edit.png'
  },
  {
    icon: Sparkles,
    title: 'Generate AI Video',
    description:
      'Create script, voiceover, captions, and full video automatically.',
    image: '/assets/homepage/chrome-ext/5-aiscript.png'
  }
];

const CHROME_WEB_STORE_URL =
  'https://chrome.google.com/webstore/detail/pigobokbbehjdnbbflmnocjnfoonefbb';

export default function ChromeExtensionSection() {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startProgress = useCallback(() => {
    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);
    progressRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        return prev + 100 / (ROTATE_INTERVAL / 50);
      });
    }, 50);
  }, []);

  const startAutoRotate = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    startProgress();
    intervalRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % cards.length);
      startProgress();
    }, ROTATE_INTERVAL);
  }, [startProgress]);

  const handleCardClick = useCallback(
    (index: number) => {
      setActive(index);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(0);
      startProgress();
      timeoutRef.current = setTimeout(() => {
        startAutoRotate();
      }, PAUSE_DURATION);
    },
    [startAutoRotate, startProgress]
  );

  useEffect(() => {
    startAutoRotate();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [startAutoRotate]);

  const current = cards[active];

  return (
    <section
      id="chrome-extension"
      className="relative py-14 sm:py-20 md:py-24 overflow-hidden"
    >
      {/* Layered background */}
      <div className="absolute inset-0 bg-section-green-gradient" />

      {/* Floating orbs */}
      <div className="absolute top-20 left-[10%] w-72 h-72 bg-primary/[0.06] rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-20 right-[5%] w-96 h-96 bg-primary/[0.04] rounded-full blur-[120px] pointer-events-none" />

      <div className="relative mx-auto max-w-6xl px-4 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-14 md:mb-16"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 px-3 py-1 text-xs font-medium text-primary mb-5 bg-videomule-light-green dark:bg-primary/10">
            <Chrome className="h-3.5 w-3.5" />
            Chrome Extension
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground leading-[1.08] mb-4 sm:mb-5">
            Create Videos Directly
            <br />
            from Your Browser
            <span className="text-primary">.</span>
          </h2>
          <p className="mx-auto max-w-xl text-sm sm:text-base md:text-lg text-muted-foreground leading-relaxed">
            Record your screen, preview instantly, then edit or generate a
            complete AI video all in one seamless flow.
          </p>
        </motion.div>

        {/* Browser mockup */}
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="mb-6 sm:mb-8"
        >
          <div className="relative group">
            <div className="absolute -inset-1 bg-linear-to-b from-primary/20 via-primary/5 to-transparent rounded-[20px] blur-sm opacity-60 group-hover:opacity-80 transition-opacity duration-500" />

            <div className="relative rounded-2xl border border-border/80 bg-card overflow-hidden shadow-2xl shadow-primary/[0.08]">
              {/* Tab Bar */}
              <div className="flex items-center gap-0 bg-muted/60 border-b border-border/40 pt-2 px-2">
                <div className="relative flex items-center gap-2 bg-card border border-border/60 border-b-0 rounded-t-lg px-3 sm:px-4 py-1.5 sm:py-2 max-w-[180px] sm:max-w-[220px]">
                  <img
                    src={vmLogoSrc}
                    alt="VideoMule"
                    className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 object-contain"
                  />
                  <span className="text-[10px] sm:text-xs font-medium text-foreground truncate">
                    VideoMule
                  </span>
                  <button className="ml-auto text-muted-foreground/50 hover:text-foreground text-[10px] leading-none">
                    &times;
                  </button>
                </div>
                <div className="flex items-center gap-1 px-2 opacity-40">
                  <span className="text-muted-foreground text-lg leading-none">
                    +
                  </span>
                </div>
                <div className="ml-auto flex items-center gap-1.5 sm:gap-2 pr-2 pb-1">
                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-400/70 border border-green-500/30" />
                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-400/70 border border-yellow-500/30" />
                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-400/70 border border-red-500/30" />
                </div>
              </div>

              {/* Address Bar */}
              <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-muted/30 border-b border-border/50">
                <div className="hidden sm:flex items-center gap-1.5">
                  <button className="p-1 rounded hover:bg-muted/60 text-muted-foreground/50">
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <button className="p-1 rounded hover:bg-muted/60 text-muted-foreground/50">
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                  <button className="p-1 rounded hover:bg-muted/60 text-muted-foreground/50">
                    <RefreshCw className="h-3 w-3" />
                  </button>
                </div>
                <div className="flex-1 flex items-center h-6 sm:h-7 rounded-full bg-muted/50 border border-border/40 px-2 sm:px-3 gap-1.5 sm:gap-2">
                  <Lock className="h-3 w-3 text-primary/60" />
                  <span className="text-[10px] sm:text-[11px] text-muted-foreground/70 font-medium">
                    videomule.com
                  </span>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center gap-1.5 sm:gap-2 rounded-full bg-primary/10 border border-primary/20 px-2 sm:px-3 py-0.5 sm:py-1"
                  >
                    <span className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-primary text-primary-foreground text-[9px] sm:text-[10px] font-bold">
                      {active + 1}
                    </span>
                    <span className="text-[10px] sm:text-xs font-semibold text-foreground whitespace-nowrap hidden sm:inline">
                      {current.title}
                    </span>
                  </motion.div>
                </AnimatePresence>
                <button className="hidden sm:block p-1 rounded hover:bg-muted/60 text-muted-foreground/40">
                  <Star className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Image area */}
              <div className="relative bg-muted/10 dark:bg-black/40 aspect-[16/10] overflow-hidden">
                <div className="absolute inset-0 bg-linear-to-b from-muted/5 via-transparent to-muted/10 dark:from-black/20 dark:via-transparent dark:to-black/30 z-10 pointer-events-none" />
                <AnimatePresence mode="wait">
                  <motion.img
                    key={active}
                    src={current.image}
                    alt={current.title}
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.45, ease: 'easeInOut' }}
                    className="w-full h-full object-contain absolute inset-0"
                  />
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Cards Grid — 5 columns on large */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4"
        >
          {cards.map((card, i) => {
            const Icon = card.icon;
            const isActive = i === active;
            return (
              <motion.button
                key={card.title}
                onClick={() => handleCardClick(i)}
                whileHover={{ y: -3, scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                transition={{
                  duration: 0.25,
                  type: 'spring',
                  stiffness: 300
                }}
                className={`group relative text-left rounded-2xl border p-4 sm:p-5 md:p-6 transition-all duration-400 cursor-pointer overflow-hidden ${
                  isActive
                    ? 'border-primary/40 bg-linear-to-br from-primary/10 via-primary/[0.04] to-transparent shadow-xl shadow-primary/10 ring-1 ring-primary/10'
                    : 'border-border/50 bg-card/90 backdrop-blur-sm hover:border-primary/25 hover:shadow-lg hover:shadow-primary/[0.05]'
                }`}
              >
                {/* Progress bar */}
                <div className="absolute top-0 left-0 right-0 h-[2.5px] bg-border/20 overflow-hidden rounded-t-2xl">
                  <motion.div
                    className="h-full bg-linear-to-r from-primary via-primary/80 to-primary/60 rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: isActive ? `${progress}%` : '0%' }}
                    transition={{ duration: 0.05, ease: 'linear' }}
                  />
                </div>

                {isActive && (
                  <div className="absolute -top-8 -right-8 w-24 h-24 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
                )}

                <span
                  className={`absolute top-3 right-3 sm:top-4 sm:right-4 text-[10px] sm:text-[11px] font-bold tracking-wide transition-colors duration-300 ${
                    isActive
                      ? 'text-primary/70'
                      : 'text-muted-foreground/20 group-hover:text-muted-foreground/35'
                  }`}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>

                <motion.div
                  animate={isActive ? { rotate: [0, -6, 6, 0] } : {}}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center mb-3 sm:mb-4 transition-all duration-300 ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30'
                      : 'bg-muted/50 border border-border/50 text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary group-hover:border-primary/20'
                  }`}
                >
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                </motion.div>

                <h4
                  className={`text-xs sm:text-sm md:text-base font-semibold tracking-tight mb-1.5 sm:mb-2 transition-colors duration-300 leading-snug ${
                    isActive
                      ? 'text-foreground'
                      : 'text-foreground/75 group-hover:text-foreground/90'
                  }`}
                >
                  {card.title}
                </h4>
                <p
                  className={`text-[10px] sm:text-xs md:text-sm leading-relaxed line-clamp-3 transition-colors duration-300 ${
                    isActive
                      ? 'text-muted-foreground/90'
                      : 'text-muted-foreground/60'
                  }`}
                >
                  {card.description}
                </p>
              </motion.button>
            );
          })}
        </motion.div>

        {/* Mobile dot indicators */}
        <div className="flex items-center justify-center gap-2 mt-6 lg:hidden">
          {cards.map((_, i) => (
            <button
              key={i}
              onClick={() => handleCardClick(i)}
              className={`rounded-full transition-all duration-300 ${
                i === active
                  ? 'w-6 h-2 bg-primary'
                  : 'w-2 h-2 bg-muted-foreground/20 hover:bg-muted-foreground/40'
              }`}
            />
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-10 sm:mt-12 text-center space-y-6"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="h-px w-12 bg-linear-to-r from-transparent to-border" />
            <div className="h-px w-12 bg-linear-to-l from-transparent to-border" />
          </div>
          <a
            href={CHROME_WEB_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              size="lg"
              className="group text-base font-semibold gap-3 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
            >
              <Chrome className="h-5 w-5" />
              Add to Chrome
              <ArrowUpRight className="h-4 w-4" />
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
